import React from "react";

/** Map backend/platform keys to meet | zoom | teams | gcal. */
export function normalizeUsagePlatformKey(platform) {
  const k = String(platform || "").toLowerCase().replace(/[\s_-]+/g, "");
  if (k.includes("zoom")) return "zoom";
  if (k.includes("team")) return "teams";
  if (k === "gcal" || (k.includes("calendar") && !k.includes("meet"))) return "gcal";
  if (k.includes("meet") || k.includes("google")) return "meet";
  return "meet";
}

/** Static files in public/assets — img src only (no inline SVG; CSP-friendly). */
const PUBLIC_ASSETS = `${process.env.PUBLIC_URL || ""}/assets`;

/** Official product marks in public/assets (Meet/Teams/Zoom SVG). */
const MEETING_LIST_PLATFORM_ICON = {
  meet: `${PUBLIC_ASSETS}/google-meet.svg`,
  zoom: `${PUBLIC_ASSETS}/zoom.svg`,
  teams: `${PUBLIC_ASSETS}/microsoft-teams.svg`,
  gcal: `${PUBLIC_ASSETS}/google-calendar.svg`,
};

const MEETING_LIST_PLATFORM_TITLE = {
  meet: "Google Meet",
  zoom: "Zoom",
  teams: "Microsoft Teams",
  gcal: "Google Calendar",
};

function PlatformIconImg({ src, size, title, wrapClassName }) {
  return (
    <span className={wrapClassName} title={title}>
      <img
        className="platform-brand-icon__img"
        src={src}
        alt=""
        width={size}
        height={size}
        draggable={false}
        decoding="async"
        loading="lazy"
      />
    </span>
  );
}

/** Official Google Meet mark, inlined so it still paints when the network is down. */
export function GoogleMeetMark({ size = 20, title = "Google Meet" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 87.5 72"
      aria-hidden={title ? undefined : true}
      role="img"
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      <path fill="#00832d" d="M49.5 36l8.53 9.75 11.47 7.33 2-17.02-2-16.64-11.69 6.44z" />
      <path fill="#0066da" d="M0 51.5V66c0 3.315 2.685 6 6 6h14.5l3-10.96-3-9.54-9.95-3z" />
      <path fill="#e94235" d="M20.5 0L0 20.5l10.55 3 9.95-3 2.95-9.41z" />
      <path fill="#2684fc" d="M20.5 20.5H0v31h20.5z" />
      <path fill="#00ac47" d="M82.6 8.68L69.5 19.42v33.66l13.16 10.79c1.97 1.54 4.85.135 4.85-2.37V11c0-2.535-2.945-3.925-4.91-2.32zM49.5 36v15.5h-29V72h43c3.315 0 6-2.685 6-6V53.08z" />
      <path fill="#ffba00" d="M63.5 0h-43v20.5h29V36l20-16.57V6c0-3.315-2.685-6-6-6z" />
    </svg>
  );
}

/** All Meetings list — official platform marks. Meet is inline; others are static files. */
export function MeetingListPlatformIcon({ platform, size = 20, className = "" }) {
  const key = normalizeUsagePlatformKey(platform);
  const title = MEETING_LIST_PLATFORM_TITLE[key] || MEETING_LIST_PLATFORM_TITLE.meet;
  if (key === "meet") {
    return (
      <span className={["meet-platform-brand-icon", className].filter(Boolean).join(" ")} title={title}>
        <GoogleMeetMark size={size} title="" />
      </span>
    );
  }
  const src = MEETING_LIST_PLATFORM_ICON[key] || MEETING_LIST_PLATFORM_ICON.meet;
  const wrapClassName = ["meet-platform-brand-icon", className].filter(Boolean).join(" ");
  return <PlatformIconImg src={src} size={size} title={title} wrapClassName={wrapClassName} />;
}

/** Small consistent brand icons for Usage by Platform (Meet, Zoom, Teams). */
export function PlatformBrandIcon({ platform, size = 14, className = "" }) {
  const key = normalizeUsagePlatformKey(platform);
  const src = MEETING_LIST_PLATFORM_ICON[key] || MEETING_LIST_PLATFORM_ICON.meet;
  const title = MEETING_LIST_PLATFORM_TITLE[key] || MEETING_LIST_PLATFORM_TITLE.meet;
  const wrapClassName = ["ub-platform-brand-icon", className].filter(Boolean).join(" ");
  return <PlatformIconImg src={src} size={size} title={title} wrapClassName={wrapClassName} />;
}
